import React from 'react';
import FileUploadIcon from '@mui/icons-material/FileUpload';

import FormattedName from './ProductNameColumn';
import { IProduct } from 'api/bitedb/products/products';

interface ISingleResultRow {
  keyVal?: number;
  onToggle?: () => void;
  transaction?: any;
  selectedItems?: any;
  setSelectedItem?: any;
  aiInProgress?: boolean;
  uploadModal: (item: IProduct) => void;
}

const firstNonBlankValue = (...values: unknown[]): string | null => {
  for (const value of values) {
    if (value === null || value === undefined) continue;

    const normalized = String(value).trim();
    if (normalized) return normalized;
  }

  return null;
};

const firstFiniteNumber = (...values: unknown[]): number | null => {
  for (const value of values) {
    if (
      value === null ||
      value === undefined ||
      (typeof value === 'string' && !value.trim())
    )
      continue;

    const normalized =
      typeof value === 'number' ? value : Number(String(value).trim());
    if (Number.isFinite(normalized)) return normalized;
  }

  return null;
};

const formatCurrency = (value: number | null): string => {
  if (value === null) return '-';

  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const withResolvedBrokerProductValue = (
  transaction: IProduct,
): IProduct => {
  if (!transaction) return transaction;

  const tariffInputs = transaction.tariff_result?.inputs ?? {};
  const directValue = firstFiniteNumber(transaction.product_value);
  const resolvedValue = firstFiniteNumber(
    transaction.product_value,
    tariffInputs.productValue,
    tariffInputs.product_value,
  );

  return directValue === null && resolvedValue !== null
    ? { ...transaction, product_value: resolvedValue }
    : transaction;
};

const ProductPageRow: React.FC<ISingleResultRow> = ({
  transaction,
  selectedItems,
  setSelectedItem,
  aiInProgress,
  uploadModal,
}) => {
  const handleCheckboxClick = (item: any) => {
    setSelectedItem(item);
  };

  const formatHTSCode = (code: string | null | undefined): string => {
    if (!code) {
      return 'No Code';
    }

    // 1. Remove all non-digit characters (dots, spaces, dashes, etc.)
    const cleanCode = code.toString().replace(/\D/g, '');

    // 2. Check if we have the standard 10-digit HTS code
    if (cleanCode.length === 10) {
      // Format: xxxx.xx.xxxx
      return `${cleanCode.slice(0, 4)}.${cleanCode.slice(
        4,
        6,
      )}.${cleanCode.slice(6)}`;
    }

    // 3. Fallback: If it's not 10 digits (e.g. 8 digits or 6 digits), return as is or apply partial formatting
    // Returning original 'clean' version or the original input is safer than forcing a format that doesn't fit.
    return code;
  };

  const additionalDuties = () => {
    const rates = [0.0];
    if (
      transaction?.tariff_result?.results?.lines &&
      transaction?.tariff_result?.results?.lines.length > 0
    ) {
      transaction?.tariff_result?.results?.lines.forEach((line: any) => {
        if (line.rows) {
          line.rows.forEach((row: any) => {
            if (row.rate.includes('%')) {
              rates.push(Number.parseFloat(row.rate.replace('%', '')));
            }
          });
        }
      });
    }
    return `${rates
      .reduce((accumulator, currentValue) => accumulator + currentValue, 0)
      .toFixed(2)}%`;
  };

  const hasInvalidHTS =
    transaction?.tariff_result?.status?.message === 'Invalid Country of Origin';
  const tariffInputs = transaction?.tariff_result?.inputs ?? {};
  const htsCode = firstNonBlankValue(
    transaction?.hts_code,
    tariffInputs.htsCode,
    tariffInputs.hts_code,
  );
  const selectableTransaction = withResolvedBrokerProductValue(transaction);
  const productValue = firstFiniteNumber(
    selectableTransaction?.product_value,
  );

  return (
    <>
      <div className='screening-results-row'>
        <div
          className='screening-results-row-default-cell'
          style={{ maxWidth: '48px' }}
        >
          <div
            className='screening-results-row-default-cell-inner'
            style={{ width: '48px' }}
            onClick={() =>
              handleCheckboxClick(
                selectableTransaction ? selectableTransaction : [],
              )
            }
          >
            {selectedItems?.some(
              (item: { id: void }) => item.id === transaction?.id,
            ) ? (
              <div className='full-list-filters-cont-row-icon-item'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='12'
                  height='12'
                  viewBox='0 0 12 12'
                  fill='none'
                >
                  <path
                    d='M10 3L4.5 8.5L2 6'
                    stroke='var(--text-primary)'
                    strokeWidth='1.6666'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
            ) : (
              <div className='full-list-filters-cont-row-icon-item-unselected'></div>
            )}
          </div>
        </div>
        <div className='screening-results-row-default-cell'>
          <div className='screening-results-row-default-cell-inner'>
            <FormattedName
              transaction={transaction}
              //viewType={viewType || 'Product View'}
              //location={location || `/product/${transaction.id}`}
              //handleEdit={handleEdit}
            />
          </div>
        </div>
        <div className='screening-results-row-default-cell'>
          <div
            className='screening-results-row-default-cell-inner'
            title={hasInvalidHTS ? 'Invalid HTS/Country Combination' : ''}
          >
            <div
              className='screening-results-row-default-cell-text'
              style={
                htsCode && !hasInvalidHTS
                  ? {}
                  : { color: 'var(--mid-color)' }
              }
            >
              {formatHTSCode(htsCode)}
            </div>
          </div>
        </div>
        <div className='screening-results-row-default-cell'>
          <div
            className='screening-results-row-default-cell-inner'
            title={hasInvalidHTS ? 'Invalid HTS/Country Combination' : ''}
          >
            <div
              className='screening-results-row-default-cell-text'
              style={
                transaction?.country_of_origin &&
                transaction?.country_of_origin !== '' &&
                !hasInvalidHTS
                  ? {}
                  : { color: 'var(--mid-color)' }
              }
            >
              {transaction?.country_of_origin}
            </div>
          </div>
        </div>
        <div className='screening-results-row-default-cell'>
          <div className='screening-results-row-default-cell-inner'>
            <div className='screening-results-row-default-cell-text'>
              {formatCurrency(productValue)}
            </div>
          </div>
        </div>
        <div className='screening-results-row-default-cell'>
          <div className='screening-results-row-default-cell-inner'>
            <div
              className='screening-results-row-default-cell-text'
              style={
                transaction?.tariff_result?.base?.baseDutyRate &&
                (transaction?.tariff_result?.base?.baseDutyRate === 'Free' ||
                  transaction?.tariff_result?.base?.baseDutyRate === '0%')
                  ? {}
                  : { color: 'var(--mid-color)' }
              }
            >
              {transaction?.tariff_result?.base?.baseDutyRate || '-'}
            </div>
          </div>
        </div>
        <div className='screening-results-row-default-cell'>
          <div className='screening-results-row-default-cell-inner'>
            <div
              className='screening-results-row-default-cell-text'
              style={
                additionalDuties() === '0.00%'
                  ? {}
                  : { color: 'var(--mid-color)' }
              }
            >
              {additionalDuties()}
            </div>
          </div>
        </div>
        <div className='screening-results-row-default-cell'>
          <div className='screening-results-row-default-cell-inner'>
            <div
              className='screening-results-row-default-cell-text'
              style={
                transaction?.compliance_score &&
                transaction?.compliance_score < 60
                  ? { color: 'var(--low-color)' }
                  : transaction?.compliance_score &&
                    transaction?.compliance_score < 90
                  ? { color: 'var(--mid-color)' }
                  : {}
              }
            >
              {aiInProgress && (
                <span style={{ color: 'var(--mid-color)', fontSize: '12px' }}>
                  Processing...
                </span>
              )}
              {transaction?.compliance_score || 'No Assessment'}
            </div>
          </div>
        </div>
        <div className='screening-results-row-default-cell'>
          <div className='screening-results-row-default-cell-inner'>
            <div className='screening-results-row-default-cell-text'>
              <FileUploadIcon
                onClick={() => uploadModal(transaction as IProduct)}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductPageRow;
